import { ArrowLeft, ArrowUpRight, LayoutGrid, Palette } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useWorkspace } from "@/app/WorkspaceContext";
import { useThemes } from "@/data/hooks";
import { DesignControls } from "./DesignControls";

/**
 * Designs — the brand/theme gallery route. Now a thin shell around the shared
 * <DesignControls/> (which also powers Make's "Design" tab), plus page chrome
 * and a back button so a marketer who lands here directly can return to Make.
 */
export function DesignsScreen() {
  const nav = useNavigate();
  const { currentProjectId } = useWorkspace();
  const themesQ = useThemes(currentProjectId);
  const activeName = (themesQ.data?.items ?? []).find((t) => t.is_active)?.name;

  return (
    <div className="stagger space-y-6 pb-4">
      <button
        type="button"
        onClick={() => nav("/make")}
        className="inline-flex items-center gap-1.5 self-start rounded-btn px-2 py-1 text-[13px] font-medium text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        Back to Make
      </button>

      <Header activeName={activeName} />

      <button
        type="button"
        onClick={() => nav("/layouts")}
        className="flex w-full items-center justify-between gap-2 rounded-card border border-hairline bg-paper-2/50 px-4 py-3 text-left transition-colors hover:border-ink/20 dark:bg-white/5"
      >
        <span className="flex items-center gap-2.5">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-accent-soft text-accent">
            <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <span>
            <span className="block text-[13.5px] font-medium text-ink-strong">Browse layouts</span>
            <span className="block text-[12px] text-muted">Change a page's structure — bento, editorial, feature grid…</span>
          </span>
        </span>
        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} />
      </button>

      {/* Key by project so the draft (colors / fonts / selected preset) reseeds
          from the active theme when the workspace switches — never carries one
          project's in-progress theme onto another. */}
      <DesignControls key={currentProjectId ?? "none"} variant="page" />
    </div>
  );
}

/* --------------------------------- header --------------------------------- */
function Header({ activeName }: { activeName?: string }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-accent-soft text-accent">
          <Palette className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div>
          <p className="eyebrow mb-1">Designs</p>
          <h1 className="text-[24px] font-semibold tracking-tight text-ink-strong md:text-[28px]">
            Your brand, your way.
          </h1>
        </div>
      </div>
      {activeName && (
        <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-hairline px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-accent">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
          {activeName} · live
        </span>
      )}
    </header>
  );
}
